import { post } from '../../../../../api/baseUrl';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../store';
import { useGlobalLoading } from '../../../../../context';
import { MedicationCatalog } from '../types';

export interface GetAllCatalogMedsPayload {
  medName?: string;
  brand?: string;
  indication?: string;
  therapeuticArea?: string;
  therapeuticClass?: string;
  isDoseSchedule?: boolean;
  isPrn?: boolean;
  isTaper?: boolean;
  mpIds?: string[];
  // tenancyId: number;
  page?: number;
  pageSize?: number;
  includeTotalPages?: boolean;
}

export const useGetAllCatalogMeds = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const { startLoading, stopLoading } = useGlobalLoading();
  const [isLoading, setIsLoading] = useState(false);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const [medicineData, setMedicineData] = useState<MedicationCatalog[]>([]);
  const [totalPages, setTotalPages] = useState<number>();
  const [medCatalogData, setMedCatalogData] = useState<
    Record<string, MedicationCatalog[]>[]
  >([]);

  const postGetAllCatalogMeds = useCallback(
    async ({
      filterPayload = {},
      medProfileCatalogData = false,
    }: {
      filterPayload: GetAllCatalogMedsPayload;
      medProfileCatalogData?: boolean;
    }) => {
      if (!selectedTenancy) return;
      setIsLoading(true);
      try {
        startLoading();
        const payload = filterPayload
          ? {
              ...filterPayload,
              tenancyId: selectedTenancy.tenancy_id,
            }
          : {
              tenancyId: selectedTenancy.tenancy_id,
              page: 1,
              pageSize: 30,
            };
        const {
          data: { data },
        } = await post(`getAllCatalogMeds`, { ...payload });
        const nextMedicineData = data.data || [];
        const nextTotalPages =
          typeof data?.totalPages === 'number'
            ? data.totalPages
            : typeof data?.pagination?.totalPages === 'number'
              ? data.pagination.totalPages
              : typeof data?.meta?.totalPages === 'number'
                ? data.meta.totalPages
                : undefined;
        if (typeof nextTotalPages === 'number') {
          setTotalPages(nextTotalPages);
          (nextMedicineData as any).__totalPages = nextTotalPages;
        }
        stopLoading();
        if (medProfileCatalogData) {
          setMedCatalogData(nextMedicineData || []);
        } else {
          setMedicineData(nextMedicineData || []);
        }
        return nextMedicineData;
      } catch (error: any) {
        console.error('An error occurred during getAllCatalogMeds', error);
        setErrorMessage(
          error?.response?.data?.message ||
            'Unknown error in getAllCatalogMeds',
        );
        stopLoading();
      } finally {
        setIsLoading(false);
      }
    },
    [selectedTenancy, startLoading, stopLoading],
  );

  return {
    postGetAllCatalogMeds,
    errorMessage,
    isLoading,
    medicineData: Array.isArray(medicineData) ? medicineData : [],
    totalPages,
    medCatalogData,
  };
};
